import { Cross2Icon, ExclamationTriangleIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { useUsage } from "../../contexts/UsageContext";
import { useUsageWarning } from "../../hooks/useUsageWarning";
import { formatResetDate } from "../../utils/usageMonths";
import { PANEL_STYLE } from "./tokens";
import UpgradeForm from "./UpgradeForm";

/**
 * Free plan usage notice at the top of every dashboard page. ChatGPT's usage notes and block
 * message send people to the dashboard root "to learn more about usage and resets", so whichever
 * page they land on has to answer that: how much is used and when it comes back.
 *
 * Deliberately quiet: a neutral strip with a text link, so it informs without competing with the
 * page's own primary action (Add a device, the plan cards). From the warn threshold it can be
 * dismissed for the rest of the period; at the limit it stays, because calls are paused.
 */
export default function UsageLimitBanner() {
    const { usage } = useUsage();
    const { visible, atLimit, dismiss } = useUsageWarning();

    if (!usage || usage.callsIncluded === null || usage.pct === null || !visible) {
        return null;
    }

    const resetDate = formatResetDate(usage.resetsAt);
    const message = atLimit
        ? `You've used all ${usage.callsIncluded.toLocaleString()} free tool calls this month. They come back on ${resetDate}.`
        : `You've used ${Math.floor(usage.pct)}% of this month's free tool calls. They reset on ${resetDate}.`;
    const Icon = atLimit ? ExclamationTriangleIcon : InfoCircledIcon;

    return (
        <Flex role="status" align="center" gap="3" wrap="wrap" mb="5" px="3" py="2" style={PANEL_STYLE}>
            <Icon
                aria-hidden="true"
                style={{
                    flex: "none",
                    color: atLimit ? "var(--red-11)" : "var(--dc-amber)",
                }}
            />
            <Text
                size="2"
                style={{
                    flex: "1 1 240px",
                    minWidth: 0,
                }}
            >
                {message}
            </Text>
            <Flex
                align="center"
                gap="3"
                style={{
                    flex: "none",
                }}
            >
                <UpgradeForm
                    surface="limit_banner"
                    size="1"
                    variant="ghost"
                    style={{
                        margin: 0,
                    }}
                />
                {!atLimit && (
                    <IconButton
                        type="button"
                        size="1"
                        variant="ghost"
                        color="gray"
                        aria-label="Dismiss until the next reset"
                        onClick={dismiss}
                        style={{
                            margin: 0,
                        }}
                    >
                        <Cross2Icon />
                    </IconButton>
                )}
            </Flex>
        </Flex>
    );
}
